import { Switch } from "../switch";
import { Label } from "../label";
import { useEffect, useRef, useState } from "react";
import { HGIntervals } from "@/common/types";
import {
  endQualifiers,
  IntervalQualifiers as Qualifiers,
  qualifyName,
  startQualifiers,
} from "@/common/qualifiers";
import { safeName } from "@/common/recordTree";
import useStacks from "@/stack/stacks";
import { HGStack } from "@/stack/stack";
import Stacks from "./Stacks";
import { FieldLabel } from "../field";

export interface RecordFormIntervalsProps {
  id: string;
  value: HGIntervals;
  onChange(intervals: HGIntervals): void;
  /** Whether the record is placed on the time scale at all. */
  enabled: boolean;
  onEnabledChange(enabled: boolean): void;
}

export default function RecordFormIntervals({
  id,
  value,
  onChange,
  enabled,
  onEnabledChange,
}: RecordFormIntervalsProps) {
  const stacks = useStacks(value.interval, value.end_interval);
  const endStackBackup = useRef<HGStack | null>(null);
  const [isFinished, setIsFinished] = useState<boolean>(!!value.end_interval);

  // The picker is live: every click writes straight into the form value, and
  // nothing is persisted until the form itself is submitted.
  const propagateChange = (
    finished: boolean,
    qualifiers?: Partial<HGIntervals>,
  ) => {
    const interval = stacks.getInterval();
    let endInterval = stacks.getEndInterval();
    if (!endInterval && finished) {
      endInterval = interval;
    }
    const next: HGIntervals = {
      ...value,
      ...qualifiers,
      interval,
      end_interval: endInterval,
    };
    // A single interval, or an ongoing record, has no separate end date to
    // qualify -- leaving the flags set would resurrect them later.
    if (!stacks.getEndStack()) {
      next.end_interval_approximate = false;
      next.end_interval_uncertain = false;
    }
    onChange(next);
  };

  // Subscribing during render added a listener on every render and never
  // removed any. Held in refs so the subscription itself survives re-renders
  // while still seeing current values.
  const propagateChangeRef = useRef(propagateChange);
  propagateChangeRef.current = propagateChange;
  const isFinishedRef = useRef(isFinished);
  isFinishedRef.current = isFinished;

  useEffect(() => {
    const unsubscribe = stacks.subscribe(() => {
      propagateChangeRef.current(isFinishedRef.current);
    });
    return () => {
      unsubscribe();
    };
  }, [stacks]);

  useEffect(() => {
    if (!stacks) return;
    propagateChange(isFinished);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinished]);

  const endStack = stacks.getEndStack();
  const startName = safeName(stacks.getInterval());
  const endName = endStack ? safeName(endStack.getInterval()) : null;
  const startQ = startQualifiers(value);
  const endQ = endQualifiers(value);

  const preview =
    (!isFinished ? "Since " : "") +
    qualifyName(startName, startQ) +
    (endName ? ` - ${qualifyName(endName, endQ)}` : "");

  return (
    <div id={id} className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 items-center justify-between">
        <FieldLabel
          htmlFor="withTimeSwitch"
          className="cursor-pointer uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground"
        >
          On the time scale
        </FieldLabel>
        <Switch
          id="withTimeSwitch"
          checked={enabled}
          onCheckedChange={onEnabledChange}
        />
      </div>

      {!enabled ? (
        <p className="text-[12.5px] leading-relaxed text-muted-foreground">
          This record is a note: it is not placed anywhere on the time scale.
          Turn the switch on to give it a date.
        </p>
      ) : (
        <>
          <div className="flex shrink-0 items-center gap-2">
            <Switch
              id="notFinishedSwitch"
              checked={!isFinished}
              onCheckedChange={(checked) => {
                if (checked) {
                  if (endStack) {
                    endStackBackup.current = endStack;
                  }
                  stacks.removeEndStack();
                  setIsFinished(false);
                } else {
                  setIsFinished(true);
                  if (endStackBackup.current) {
                    stacks.setEndStack(endStackBackup.current);
                    stacks.setFocus("end");
                  } else {
                    // Seeds one interval past the start, at the start's own
                    // level: an end equal to the start collapses straight back,
                    // and jumping to some other level would move the list away
                    // from where the user is looking.
                    stacks.addEndStack();
                  }
                }
              }}
            />
            <Label
              htmlFor="notFinishedSwitch"
              className="cursor-pointer text-muted-foreground text-sm whitespace-nowrap"
            >
              Ongoing
            </Label>
          </div>

          {/* No outer scroller: the widget virtualises its own list, and
              wrapping it in one defeats the windowing. The floor is kept low so
              a short viewport squeezes the list rather than overflowing the tab,
              which has no scroller of its own. */}
          <div className="min-h-[160px] flex-1 overflow-hidden rounded-lg border">
            <Stacks
              stacks={stacks}
              singleMode={!isFinished}
              // The picker's From/To chips are the endpoint labels, so the
              // qualifiers hang off them there rather than being repeated here.
              qualifiers={{
                start: startQ,
                end: endQ,
                onChange: (focus, q: Qualifiers) =>
                  propagateChange(
                    isFinished,
                    focus === "end"
                      ? {
                          end_interval_approximate: !!q.approximate,
                          end_interval_uncertain: !!q.uncertain,
                        }
                      : {
                          interval_approximate: !!q.approximate,
                          interval_uncertain: !!q.uncertain,
                        },
                  ),
              }}
            />
          </div>

          <p className="shrink-0 truncate font-mono text-[12px] tracking-[.01em] text-muted-foreground">
            {preview}
          </p>
        </>
      )}
    </div>
  );
}
